import React, { useEffect, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { useAuth } from '../../contexts/AuthContext'
import { Container } from '../ui'
import AccountSidebarPanels from './AccountSidebarPanels'
import AccountIdentityStrip from './AccountIdentityStrip'
import { usePageTitle } from '../../hooks/usePageTitle'

/**
 * Shell for /account/* — sidebar navigation + nested route outlet.
 * On /account/watchlist the sidebar drills into list filters (WatchlistListNav).
 */
const AccountLayout: React.FC = () => {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const onWatchlistRoute = location.pathname.startsWith('/account/watchlist')
  const [watchlistSidebarOpen, setWatchlistSidebarOpen] = useState(true)
  const prevPathRef = useRef(location.pathname)

  useEffect(() => {
    const prev = prevPathRef.current
    prevPathRef.current = location.pathname
    if (onWatchlistRoute && !prev.startsWith('/account/watchlist')) {
      setWatchlistSidebarOpen(true)
    }
  }, [location.pathname, onWatchlistRoute])

  const showWatchlistSidebar = onWatchlistRoute && watchlistSidebarOpen

  usePageTitle('Konto')

  useEffect(() => {
    if (location.hash === '#notification-settings') {
      navigate('/account/notifikationer/indstillinger', { replace: true })
    }
  }, [location.hash, navigate])

  if (!user) return null

  async function handleLogout() {
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <Container className="pt-7 pb-16">

      <div className="flex flex-col md:flex-row md:gap-10">
        <div className="md:w-60 shrink-0">
          <AccountIdentityStrip name={user.name} email={user.email} />
          <div className="mt-4">
            <AccountSidebarPanels
              onWatchlist={showWatchlistSidebar}
              isAdmin={Boolean(user.isAdmin)}
              onLogout={() => void handleLogout()}
              onWatchlistBack={() => setWatchlistSidebarOpen(false)}
              onWatchlistDrill={() => setWatchlistSidebarOpen(true)}
            />
          </div>
        </div>
        <div className="flex-1 min-w-0 mt-8 md:mt-0">
          <Outlet />
        </div>
      </div>
    </Container>
  )
}

export default AccountLayout
